import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import TagColorSwatches from "./TagColorSwatches";
import type { LeadTagColor } from "../../../../agent/src/core/leadTag/types";
import { LEAD_TAG_NAME_MAX_LENGTH } from "../../../../agent/src/helpers/normalizeLeadTagName";

type LeadTagDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; color: LeadTagColor }) => void;
  isSubmitting: boolean;
  initialData?: { name: string; color: LeadTagColor } | null;
};

const LeadTagDialog = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  initialData,
}: LeadTagDialogProps) => {
  const [name, setName] = useState("");
  const [color, setColor] = useState<LeadTagColor>("purple");

  useEffect(() => {
    if (isOpen) {
      setName(initialData?.name ?? "");
      setColor(initialData?.color ?? "purple");
    }
  }, [isOpen, initialData]);

  const handleSubmit = () => {
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), color });
  };

  const isEdit = !!initialData;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="h-full max-h-full md:h-auto md:max-h-[85vh] md:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar etiqueta" : "Nueva etiqueta"}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="lead-tag-name">Nombre</Label>
            <Input
              id="lead-tag-name"
              autoFocus
              value={name}
              maxLength={LEAD_TAG_NAME_MAX_LENGTH}
              placeholder="Ej: Inversionista"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSubmit();
              }}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label id="lead-tag-color">Color</Label>
            <TagColorSwatches value={color} onChange={setColor} labelledBy="lead-tag-color" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !name.trim()}>
            {isSubmitting ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear etiqueta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LeadTagDialog;
